import dayjs from 'dayjs';
import { Formik, Form } from 'formik';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SyncIcon from '@mui/icons-material/Sync';
import { Grid, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { Autocomplete, Button, TextField } from 'shared';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import JoyrideTour from 'components/Onboarding/Tour/JoyRideTour';

import {
    purchaseActivityFiltersSchema,
    purchaseActivityFiltersInitialValues,
} from 'components/Inventory/utils/purchaseActivityFiltersSchema';
import {
    completeTour,
    TOUR_TYPES,
    selectTourStatus,
    selectTutorialPreference,
    selectIsSkipped,
} from 'store/slices/tour.slice';
import { formatNumber } from 'components/Inventory/utils/formatNumber';
import { useInventoryService } from 'store/services/inventory.service';
import { activityFiltersStyles } from 'components/Inventory/style/style';
import { activityStatuses } from 'components/Inventory/utils/activityStatuses';
import { skyBoxPurchaseSyncTutorials } from 'components/Settings/utils/SkyboxTutorials';

const PurchaseActivityFilters = ({
    data,
    selected,
    syncStatus,
    setSelected,
    setAlertData,
    setSyncStatus,
    downloadLoading,
    handleFiltersSubmit,
    handleGetInitialData,
    handleDownloadPurchaseActivity,
}) => {
    const dispatch = useDispatch();

    const classes = activityFiltersStyles();
    const companyData = useSelector((state) => state.company.data);
    const { deleteAggFileInventory, syncPurchaseActivity } = useInventoryService();
    const tourId = TOUR_TYPES.SKYBOX_INTEGRATION;
    const tourStatus = useSelector((state) => selectTourStatus(state, tourId));
    const tutorialPreference = useSelector(selectTutorialPreference);
    const isSkipped = useSelector((state) => selectIsSkipped(state, tourId));
    const [tourRun, setTourRun] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const handleClearClick = (handleReset) => {
        handleReset();
        setSelected([]);
        handleGetInitialData();
    };

    const handleFormSubmit = (values) => {
        setSelected([]);
        const filters = {
            ...values,
            startDate: values.startDate ? dayjs(values.startDate).format('YYYY-MM-DD') : null,
            endDate: values.endDate ? dayjs(values.endDate).format('YYYY-MM-DD') : null,
            status: values.status?.value,
        };
        handleFiltersSubmit(filters);
    };

    const handleDeleteClick = async () => {
        try {
            setDeleteLoading(true);
            const response = await deleteAggFileInventory(companyData.id, selected);
            handleSuccessAlert(setAlertData, response.message);
            setSelected([]);
            await handleGetInitialData();
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleSyncClick = async () => {
        try {
            setSyncStatus((prev) => ({
                ...prev,
                purchase_status: 'processing',
            }));
            const response = await syncPurchaseActivity({ company_id: companyData?.id });
            handleSuccessAlert(setAlertData, response?.detail);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    useEffect(() => {
        if (tutorialPreference === 'skipped' || isSkipped || tourStatus?.completed) {
            setTourRun(false);
        } else if (!tourStatus?.completed && !tourRun) {
            setTourRun(true);
        }
    }, [tourStatus, tutorialPreference, isSkipped]);

    return (
        <Formik
            initialValues={purchaseActivityFiltersInitialValues}
            validationSchema={purchaseActivityFiltersSchema}
            onSubmit={handleFormSubmit}
        >
            {({ values, handleReset, handleChange, setFieldValue }) => {
                return (
                    <Form>
                        <JoyrideTour
                            steps={skyBoxPurchaseSyncTutorials}
                            run={tourRun}
                            continuous={true}
                            showProgress={true}
                            showSkipButton={true}
                            styles={{
                                options: {
                                    zIndex: 10000,
                                },
                            }}
                            onFinish={() => {
                                dispatch(completeTour({ tourId, skipSaving: false }));
                            }}
                            callback={(event) => {
                                if (event.action === 'skip' || event.action === 'tour:end') {
                                    dispatch(completeTour({ tourId, skipSaving: false }));
                                    setTourRun(false);
                                }
                            }}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6} lg={3}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        value={values.startDate && dayjs(values.startDate)}
                                        onChange={(date) => setFieldValue('startDate', date)}
                                        label="Start Date"
                                        placeholder="Start Date"
                                        format="MM-DD-YYYY"
                                        sx={classes.datePicker}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        value={values.endDate && dayjs(values.endDate)}
                                        onChange={(date) => setFieldValue('endDate', date)}
                                        label="End Date"
                                        placeholder="End Date"
                                        format="MM-DD-YYYY"
                                        sx={classes.datePicker}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                    fullWidth
                                    name="amount"
                                    inlineLabel="Amount"
                                    value={values.amount}
                                    placeholder="Search by amount"
                                    customStyle={classes.inputField}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^-?\d*\.?\d*$/.test(val)) {
                                            handleChange(e);
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                    fullWidth
                                    name="referenceId"
                                    inlineLabel="Reference"
                                    value={values.referenceId}
                                    placeholder="Search by reference"
                                    onChange={handleChange}
                                    customStyle={classes.inputField}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                    fullWidth
                                    name="purchaseId"
                                    inlineLabel="Purchase"
                                    value={values.purchaseId}
                                    placeholder="Search by Purchase Id"
                                    onChange={handleChange}
                                    customStyle={classes.inputField}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                    fullWidth
                                    name="description"
                                    inlineLabel="Description"
                                    value={values.description}
                                    placeholder="Search by description (event name)"
                                    onChange={handleChange}
                                    customStyle={classes.inputField}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                    fullWidth
                                    name="venue"
                                    inlineLabel="Venue"
                                    value={values.venue}
                                    placeholder="Search by venue"
                                    onChange={handleChange}
                                    customStyle={classes.inputField}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <Autocomplete
                                    options={activityStatuses}
                                    labelKey="label"
                                    valueKey="value"
                                    placeholder="Select Status"
                                    name="status"
                                    inlineLabel="Status"
                                    value={values.status}
                                    onChange={(e, value) => setFieldValue('status', value)}
                                    isSearchField={false}
                                    disableClearable
                                    customStyle={classes.dropdown}
                                    showTotal={true}
                                />
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Grid item>
                                <Button
                                    text="Download CSV"
                                    loading={downloadLoading}
                                    customStyles={classes.clearButton}
                                    onClick={handleDownloadPurchaseActivity}
                                />
                                {syncStatus?.company_pos && (
                                    <Button
                                        text="Sync Purchase Data"
                                        startIcon={<SyncIcon />}
                                        loading={
                                            syncStatus?.purchase_status &&
                                            syncStatus?.purchase_status != 'completed'
                                        }
                                        onClick={handleSyncClick}
                                        data-tour="skybox-purchase-sync-button"
                                    />
                                )}
                            </Grid>

                            <Grid item marginTop="10px" marginBottom="10px" marginLeft="auto">
                                {selected?.length > 0 && (
                                    <Button
                                        text="Delete All"
                                        loading={deleteLoading}
                                        onClick={handleDeleteClick}
                                        customStyles={classes.deleteButton}
                                    />
                                )}
                                <Button
                                    text="Clear"
                                    onClick={() => handleClearClick(handleReset)}
                                    customStyles={classes.clearButton}
                                />
                                <Button text="Apply" type="submit" />
                            </Grid>
                        </Grid>

                        {data?.count > 0 && (
                            <Grid container sx={classes.countWrapper}>
                                <Typography fontSize="18px">{`TOTAL: ${
                                    data?.count
                                } ROWS ($${formatNumber(
                                    data?.totals?.total_purchase_price,
                                )})`}</Typography>
                            </Grid>
                        )}
                    </Form>
                );
            }}
        </Formik>
    );
};

export default PurchaseActivityFilters;
